import type { ReactNode } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TablePagination,
  Box,
  Skeleton,
} from '@mui/material';
import { EmptyState } from './EmptyState';

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  width?: number | string;
  align?: 'left' | 'right' | 'center';
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  loading?: boolean;
  page: number; // 1-based
  limit: number;
  total: number;
  onPageChange: (page: number) => void;
  onLimitChange: (limit: number) => void;
  rowKey: (row: T) => string | number;
  actions?: (row: T) => ReactNode;
  emptyTitle?: string;
  emptyDescription?: string;
}

/** Generic paginated table used by every list page. */
export function DataTable<T>({
  columns,
  rows,
  loading,
  page,
  limit,
  total,
  onPageChange,
  onLimitChange,
  rowKey,
  actions,
  emptyTitle,
  emptyDescription,
}: DataTableProps<T>) {
  const colCount = columns.length + (actions ? 1 : 0);
  return (
    <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
      <TableContainer sx={{ maxHeight: 'calc(100vh - 320px)' }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align} style={{ width: c.width }}>
                  {c.label}
                </TableCell>
              ))}
              {actions && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading &&
              Array.from({ length: 6 }).map((_, i) => (
                <TableRow key={`sk-${i}`}>
                  {columns.map((c) => (
                    <TableCell key={c.key}>
                      <Skeleton variant="text" width={c.width ? undefined : `${50 + ((i * 13) % 40)}%`} />
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="right">
                      <Skeleton variant="rounded" width={64} height={24} sx={{ ml: 'auto' }} />
                    </TableCell>
                  )}
                </TableRow>
              ))}
            {!loading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={colCount} sx={{ border: 0 }}>
                  <EmptyState title={emptyTitle ?? 'No records found'} description={emptyDescription} dense />
                </TableCell>
              </TableRow>
            )}
            {!loading &&
              rows.map((row) => (
                <TableRow key={rowKey(row)} hover>
                  {columns.map((c) => (
                    <TableCell key={c.key} align={c.align}>
                      {c.render ? c.render(row) : String((row as Record<string, unknown>)[c.key] ?? '—')}
                    </TableCell>
                  ))}
                  {actions && (
                    <TableCell align="right">
                      <Box sx={{ display: 'inline-flex', gap: 0.25, justifyContent: 'flex-end' }}>{actions(row)}</Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </TableContainer>
      {total > 0 && (
        <TablePagination
          component="div"
          count={total}
          page={page - 1}
          rowsPerPage={limit}
          onPageChange={(_e, p) => onPageChange(p + 1)}
          onRowsPerPageChange={(e) => onLimitChange(Number(e.target.value))}
          rowsPerPageOptions={[10, 25, 50, 100]}
          sx={{ borderTop: '1px solid', borderColor: 'divider' }}
        />
      )}
    </Paper>
  );
}
